import {
  sanitizeTreeRuntimeDiscrepancyCohortLifecycleProbeResponse,
} from './TreeRuntimeDiscrepancyCohortLifecycleProbeContract';

import type {
  TreeRuntimeMissingParentReferenceLifecycleEvidence,
} from './TreeRuntimeDiscrepancyCohortLifecycleEvidence';

export const TREE_RUNTIME_LIFECYCLE_BROWSER_OWNER_MODES =
  Object.freeze([
    'ACTIVE_CLAIM_SINGLE_OWNER',
    'SELF_FALLBACK',
    'INVITER_PROTECTION',
    'MULTI_CLAIM_AMBIGUOUS',
    'INDETERMINATE',
  ] as const);

export type TreeRuntimeLifecycleBrowserOwnerMode =
  typeof TREE_RUNTIME_LIFECYCLE_BROWSER_OWNER_MODES[number];

export const TREE_RUNTIME_LIFECYCLE_ATTRIBUTION_STATUSES =
  Object.freeze([
    'ATTRIBUTED',
    'INVALID_BROWSER_COHORT_COUNT',
    'OWNER_RESOLUTION_INCOMPATIBLE',
    'INVALID_SERVER_PROBE_RESPONSE',
    'SERVER_LIFECYCLE_AUTHORITY_VIOLATION',
    'AGGREGATE_COUNT_MISMATCH',
  ] as const);

export type TreeRuntimeLifecycleAttributionStatus =
  typeof TREE_RUNTIME_LIFECYCLE_ATTRIBUTION_STATUSES[number];

export interface TreeRuntimeMissingParentLifecycleAttributionInput {
  readonly browserMissingReferenceParentIdsCount:
    number;

  readonly browserOwnerResolutionMode:
    TreeRuntimeLifecycleBrowserOwnerMode;

  readonly probeResponse:
    unknown;
}

export interface TreeRuntimeMissingParentLifecycleAttributionDecision {
  readonly status:
    TreeRuntimeLifecycleAttributionStatus;

  readonly readOnly: true;
  readonly repairExecuted: false;

  /*
   * Aggregate equality can authorize count attribution.
   * It can never prove the browser and server observed
   * the identical UUID cohort.
   */
  readonly identityParityProven: false;

  readonly aggregateCountParityProven:
    boolean;

  readonly lifecycleAttributionEligible:
    boolean;

  readonly browserOwnerResolutionMode:
    TreeRuntimeLifecycleBrowserOwnerMode;

  readonly browserMissingReferenceParentIdsCount:
    number | null;

  readonly serverMissingReferenceParentIdsCount:
    number | null;

  readonly missingParentReferenceLifecycle:
    TreeRuntimeMissingParentReferenceLifecycleEvidence | null;
}

const validCount = (
  value: number,
): boolean =>
  Number.isInteger(value) &&
  value >= 0 &&
  value <= 1_000_000;

const createRejectedDecision = (
  status:
    Exclude<
      TreeRuntimeLifecycleAttributionStatus,
      'ATTRIBUTED'
    >,
  mode:
    TreeRuntimeLifecycleBrowserOwnerMode,
  browserCount:
    number | null,
  serverCount:
    number | null,
): TreeRuntimeMissingParentLifecycleAttributionDecision =>
  Object.freeze({
    status,
    readOnly: true,
    repairExecuted: false,
    identityParityProven: false,
    aggregateCountParityProven: false,
    lifecycleAttributionEligible: false,
    browserOwnerResolutionMode:
      mode,
    browserMissingReferenceParentIdsCount:
      browserCount,
    serverMissingReferenceParentIdsCount:
      serverCount,
    missingParentReferenceLifecycle:
      null,
  });

const ownerModeCompatible = (
  mode:
    TreeRuntimeLifecycleBrowserOwnerMode,
): boolean =>
  mode === 'ACTIVE_CLAIM_SINGLE_OWNER' ||
  mode === 'SELF_FALLBACK';

export const resolveTreeRuntimeMissingParentLifecycleAttribution =
  (
    input:
      TreeRuntimeMissingParentLifecycleAttributionInput,
  ): TreeRuntimeMissingParentLifecycleAttributionDecision => {
    if (
      !validCount(
        input.browserMissingReferenceParentIdsCount,
      )
    ) {
      return createRejectedDecision(
        'INVALID_BROWSER_COHORT_COUNT',
        input.browserOwnerResolutionMode,
        null,
        null,
      );
    }

    const browserCount =
      input.browserMissingReferenceParentIdsCount;

    if (
      !ownerModeCompatible(
        input.browserOwnerResolutionMode,
      )
    ) {
      return createRejectedDecision(
        'OWNER_RESOLUTION_INCOMPATIBLE',
        input.browserOwnerResolutionMode,
        browserCount,
        null,
      );
    }

    const response =
      sanitizeTreeRuntimeDiscrepancyCohortLifecycleProbeResponse(
        input.probeResponse,
      );

    if (!response) {
      return createRejectedDecision(
        'INVALID_SERVER_PROBE_RESPONSE',
        input.browserOwnerResolutionMode,
        browserCount,
        null,
      );
    }

    const lifecycle =
      response.missingParentReferenceLifecycle;

    if (
      lifecycle
        .missingParentReferenceActiveSameGraphCount !== 0 ||
      lifecycle
        .missingParentReferenceCrossGraphCount !== 0 ||
      lifecycle
        .missingParentReferencePhysicallyAbsentCount !== 0
    ) {
      return createRejectedDecision(
        'SERVER_LIFECYCLE_AUTHORITY_VIOLATION',
        input.browserOwnerResolutionMode,
        browserCount,
        null,
      );
    }

    const serverCount =
      lifecycle
        .missingParentReferenceSoftDeletedSameGraphCount +
      lifecycle
        .missingParentReferenceIndeterminateCount;

    if (
      !Number.isSafeInteger(serverCount) ||
      serverCount !== browserCount
    ) {
      return createRejectedDecision(
        'AGGREGATE_COUNT_MISMATCH',
        input.browserOwnerResolutionMode,
        browserCount,
        serverCount,
      );
    }

    return Object.freeze({
      status:
        'ATTRIBUTED',
      readOnly: true,
      repairExecuted: false,
      identityParityProven: false,
      aggregateCountParityProven: true,
      lifecycleAttributionEligible: true,
      browserOwnerResolutionMode:
        input.browserOwnerResolutionMode,
      browserMissingReferenceParentIdsCount:
        browserCount,
      serverMissingReferenceParentIdsCount:
        serverCount,
      missingParentReferenceLifecycle:
        lifecycle,
    });
  };
